import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaServer } from "react-icons/fa";
import { SERVER_URL } from "../../config";
import { Field, inputClass } from "./Shared";
import { providerOptions } from "./utils";

const ServerFormModal = ({
    open,
    editingServer,
    form,
    setForm,
    formError,
    saving,
    onCancel,
    onSubmit,
}) => {
    const [costLoading, setCostLoading] = useState(false);
    const [costNotice, setCostNotice] = useState("");

    // Auto-calc cost price = rate-per-GB (from backend, per provider) * ROM.
    // Still fully editable afterwards.
    useEffect(() => {
        if (!open || !form.provider_name || !form.rom) return;

        let cancelled = false;
        setCostLoading(true);
        setCostNotice("");

        axios
            .get(`${SERVER_URL}/Cloud/getServerCostRate.php`, {
                params: { provider_name: form.provider_name, rom: form.rom },
            })
            .then((res) => {
                if (cancelled) return;
                if (res.data.success) {
                    setForm((prev) => ({ ...prev, cost_price: res.data.data.cost_price }));
                    setCostNotice(
                        `Auto-calculated: ${res.data.data.storage_gb}GB × ${res.data.data.rate_per_gb}/GB. You can still edit it.`
                    );
                } else {
                    setCostNotice(res.data.message || "Enter the cost price manually.");
                }
            })
            .catch(() => {
                if (!cancelled) setCostNotice("Could not auto-calculate — enter the cost price manually.");
            })
            .finally(() => {
                if (!cancelled) setCostLoading(false);
            });

        return () => { cancelled = true; };
        
    }, [form.provider_name, form.rom, open]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto">
            <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl bg-white border border-slate-200 p-6" style={{ boxShadow: "0 10px 30px rgba(0,0,0,0.15)" }}>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                        <FaServer className="w-5 h-5 text-blue-600" />
                        {editingServer ? "Edit Server" : "Add New Server"}
                    </h2>
                    <button onClick={onCancel} className="text-slate-400 hover:text-slate-600">✕</button>
                </div>

                {formError && <p className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 mb-4">{formError}</p>}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Server Port" required>
                        <input type="number" placeholder="e.g. 3389" value={form.server_port} onChange={(e) => setForm({ ...form, server_port: e.target.value })} className={inputClass} />
                    </Field>

                    <Field label="Server Name" required>
                        <input type="text" placeholder="e.g. Mumbai-RDS-01" value={form.server_name} onChange={(e) => setForm({ ...form, server_name: e.target.value })} className={inputClass} />
                    </Field>

                    <Field label="Provider Name">
                        <select value={form.provider_name} onChange={(e) => setForm({ ...form, provider_name: e.target.value })} className={inputClass}>
                            <option value="">Select Provider</option>
                            {providerOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </Field>

                    <Field label="RAM">
                        <input type="text" placeholder="e.g. 32GB" value={form.ram} onChange={(e) => setForm({ ...form, ram: e.target.value })} className={inputClass} />
                    </Field>

                    <Field label="ROM / Storage">
                        <input type="text" placeholder="e.g. 512GB" value={form.rom} onChange={(e) => setForm({ ...form, rom: e.target.value })} className={inputClass} />
                    </Field>

                    <Field label="Cost Price (₹)" hint={costLoading ? "Calculating from provider rate…" : costNotice}>
                        <input type="number" placeholder="Auto-calculated from provider + ROM" value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} className={inputClass} />
                    </Field>

                    <Field label="Server User ID">
                        <input type="text" placeholder="Login username for this server" value={form.server_user_id} onChange={(e) => setForm({ ...form, server_user_id: e.target.value })} className={inputClass} />
                    </Field>

                    <Field label="Server Password">
                        <input type="text" placeholder={editingServer ? "Leave blank to keep current" : "Login password for this server"} value={form.server_password} onChange={(e) => setForm({ ...form, server_password: e.target.value })} className={inputClass} />
                    </Field>
                </div>

                <div className="flex justify-end gap-2 mt-5">
                    <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm border border-slate-200 text-slate-600">Cancel</button>
                    <button onClick={onSubmit} disabled={saving} className="px-4 py-2 rounded-lg text-sm text-white disabled:opacity-60" style={{ background: "linear-gradient(90deg, #1e40af, #0369a1)" }}>
                        {saving ? "Saving…" : editingServer ? "Update" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ServerFormModal;
